import { List } from 'react-native-paper';
import {
  ActivityIndicator,
  Platform,
  ScrollView,
  Switch,
  TouchableOpacity,
  View,
} from 'react-native';
import React, { useEffect, useState } from 'react';

import { Button } from '@components/Button';
import { CircledIcon } from '@components/CircledIcon';
import { Icon } from '@components/Icon';
import { Input } from '@components/Input';
import { LocalisedServerValue } from '@components/LocalisedServerValue';
import { Typography } from '@components/Typography';
import { firebase } from '@lib/firebase';
import { logger } from '@lib/logger';
import { requestMenuUpdate } from '@lib/api/requestMenuUpdate';
import { trackEvent } from '@utils/analytics';
import { useIsMobile } from '@hooks';
import { useMenusStore } from '@stores/menus';
import { useTheme } from '@providers/Theme';
import { useTranslation } from '@providers/I18n';
import { useUnavailabilityInterval } from '@components/RejectOrder/utils';
import { Category, Item, Menu } from '@stores/menus/types';
import {
  ItemAvailabilityData,
  changeBulkItemsAvailability,
  changeItemAvailability,
} from '@lib/api/toggleItemAvailability';
import { Modal, ModalContentLayout } from '@components/Modal';
import { RadioGroup, RadioGroupItem } from '@components/RadioGroup';
import { ToastType, useToast } from '@providers/Toast';

import { useStyles } from './styles';

type GAData = {
  item_name: string;
  menu_name: string | undefined;
  branch_id: string | undefined;
  action: string;
  source: string;
  duration?: string;
};

export const MenuContent: React.FC = () => {
  const styles = useStyles();
  const theme = useTheme();
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const {
    selectedMenu: menu,
    menuLoading: loading,
    branch,
    updateSelectedMenu,
  } = useMenusStore();
  const isMobile = useIsMobile();

  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [unavailAll, setUnavailAll] = useState(true);
  const [availAll, setAvailAll] = useState(false);
  const [duration, setDuration] = useState<RadioGroupItem | null>(null);
  const [itemAvail, setItemAvail] = useState<ItemAvailabilityData>({
    itemUUID: '',
    availability: false,
  });
  const [searchText, setSearchText] = useState('');
  const [GAEventData, setGAEventData] = useState<GAData>({
    item_name: '',
    menu_name: '',
    branch_id: '',
    action: '',
    source: 'Menu',
  });
  const searchedItems = [] as Item[];
  const BULK_ITEMS_AVAILABILITY = firebase.getRemoteConfigBoolean(
    'BULK_ITEMS_AVAILABILITY',
  );

  useEffect(() => {
    setSearchText('');
  }, [menu]);

  const itemUnavailabilityInterval: RadioGroupItem[] =
    useUnavailabilityInterval();

  const handleToggleChange = async (item: Item, availability: boolean) => {
    await setItemAvail({
      itemUUID: item.itemUUID,
      availability,
    });

    const data = {
      item_name: item.name.enName,
      menu_name: menu?.uuid,
      branch_id: branch?.branchUUID,
      action: availability ? 'available' : 'unavailable',
      source: searchText.length > 0 ? 'Menu Search' : 'Menu',
    };
    setGAEventData(data);

    if (!availability) {
      setIsOpen(true);
    } else {
      itemAvailabilityChange({ itemUUID: item.itemUUID, availability });
      trackEvent('Item Availability', GAEventData);
    }
  };

  const handleToast = (
    toastId: string,
    status: ToastType,
    title: string,
    body: string,
  ) => {
    return toast.show(
      toastId,
      status,
      <Typography>{title}</Typography>,
      <Typography>{body}</Typography>,
    );
  };

  const itemAvailabilityChange = async (data: ItemAvailabilityData) => {
    if (branch) {
      const itemToastId = data.availability
        ? `item-available-toast-${data.itemUUID}`
        : `item-unavailable-toast-${data.itemUUID}`;

      try {
        handleToast(
          itemToastId,
          'loading',
          t('menu.toast.loading.title', 'Item Availability'),
          t('menu.toast.loading.message', 'Loading....'),
        );
        setIsLoading(true);
        await changeItemAvailability(branch?.branchUUID, data);
        await updateSelectedMenu(data.itemUUID, data.availability);
        setIsLoading(false);
        resetInputs();

        handleToast(
          itemToastId,
          'success',
          t('menu.toast.loading.title', 'Item Availability'),
          t(
            'menu.toast.success.message',
            'Item availability updated successfully',
          ),
        );
      } catch (error) {
        setIsLoading(false);
        handleToast(
          itemToastId,
          'error',
          t('menu.common.toast.error.title', 'Something went wrong!'),
          t('menu.common.toast.error.message', {
            defaultValue:
              'Something went wrong while Item {{value}}, Please try again later.',
            value: itemAvail.availability ? 'disabled' : 'enabled',
          }),
        );
      }
    }
  };

  const resetInputs = () => {
    setIsOpen(false);
    setUnavailAll(true);
    setAvailAll(false);
    setDuration(null);
    setItemAvail((current) => ({
      ...current,
      unavailabilityIntervalEnum: '',
    }));
  };

  const resetConfirmationInputs = () => {
    setOpenConfirm(false);
    setUnavailAll(true);
    setAvailAll(false);
  };

  const requestUpdate = async (data: Menu) => {
    const menuToastId = `menu-update-${data.uuid}`;
    try {
      handleToast(
        menuToastId,
        'loading',
        t('menu.requestUpdate.toast.title', 'Request Update Menu'),
        t('menu.requestUpdate.toast.loading.message', 'Loading....'),
      );
      setIsLoading(true);
      await requestMenuUpdate(data.uuid);
      setIsLoading(false);
      handleToast(
        menuToastId,
        'success',
        t('menu.requestUpdate.toast.title', 'Request Update Menu'),
        t(
          'menu.requestUpdate.toast.success.message',
          'Request menu update send successfully',
        ),
      );
    } catch (error) {
      logger.log('Error requesting menu update for menu UUID:', error);
      setIsLoading(false);
      handleToast(
        menuToastId,
        'error',
        t('menu.requestUpdate.toast.error.title', 'Something went wrong!'),
        t('menu.requestUpdate.toast.error.message', {
          defaultValue:
            'Something went wrong while request update menu, Please try again later.',
        }),
      );
    }
  };

  const handleItemUnavailability = async (data: ItemAvailabilityData) => {
    try {
      await itemAvailabilityChange(itemAvail);
      // GA4 event
      GAEventData.duration = data.unavailabilityIntervalEnum;
      trackEvent('Item Availability', GAEventData);
    } catch (error) {
      logger.log('error', error);
    }
  };

  const renderMenu = (categories: Category[]) => {
    return categories.map((cat: Category) => (
      <List.Accordion
        title={
          <Typography
            weight="semiBold"
            size={{ xs: 'md', md: 'xl' }}
            style={{ color: theme.palette.grey[700] }}>
            <LocalisedServerValue value={cat?.name} />
          </Typography>
        }
        style={styles.item}
        id={cat.categoryUUID}
        key={'cat' + cat.categoryUUID}
        theme={{ colors: { primary: theme.palette.grey[700] } }}>
        <ScrollView>
          {cat.items.map((item: Item) => (
            <View style={styles.listItem} key={item.itemUUID}>
              <Typography weight="medium" variant="primary" style={{ flex: 8 }}>
                <LocalisedServerValue value={item?.name} />
              </Typography>
              <View style={styles.item}>
                <Typography
                  weight="medium"
                  variant="primary"
                  style={styles.price}>
                  {item.sizes[0].price} {t('common.egp', 'EGP')}
                </Typography>
                <Switch
                  trackColor={{
                    false: theme.palette.grey[400],
                    true: theme.palette.primary[600],
                  }}
                  thumbColor={theme.palette.base[100]}
                  onValueChange={(value) => handleToggleChange(item, value)}
                  value={item.availability}
                  {...Platform.select({
                    web: {
                      activeThumbColor: theme.palette.base[100],
                    },
                  })}
                />
              </View>
            </View>
          ))}
        </ScrollView>
      </List.Accordion>
    ));
  };

  const searchMenu = () => {
    const results = [] as Category[];
    const searchData = {
      branch_id: branch?.branchUUID,
      keyword: searchText,
    };

    menu?.categories.forEach((cat: Category) => {
      const category = {
        name: cat.name,
        categoryUUID: cat.categoryUUID,
        availability: cat.availability,
        items: [] as Item[],
      };
      cat.items.forEach((item: Item) => {
        if (
          item.name.enName.toLowerCase().includes(searchText.toLowerCase()) ||
          item.name.arName.toLowerCase().includes(searchText.toLowerCase())
        ) {
          category.items.push(item);
        }
      });
      if (category.items.length > 0) {
        const index = results.findIndex(
          (catg) => catg.categoryUUID === category.categoryUUID,
        );

        if (index === -1) {
          results.push(category);
        } else {
          results[index].items.push(...category.items);
        }
        searchedItems.push(...category.items);
      }
    });
    trackEvent('Menu Search', searchData);
    return renderMenu(results);
  };

  const handleBulkItemsUnavailable = async () => {
    const toastId = 'bulk-items-unavail-toast';
    await handleBulkItemsChange(
      toastId,
      false,
      itemAvail.unavailabilityIntervalEnum,
    );
  };

  const handleBulkItemsAvailable = async () => {
    const toastId = 'bulk-items-avail-toast';
    await handleBulkItemsChange(toastId, true);
  };

  const handleBulkItemsChange = async (
    toastId: string,
    availability: boolean,
    unavailabilityIntervalEnum?: string,
  ) => {
    if (branch) {
      const bulkItems = [...searchedItems];
      try {
        setOpenConfirm(false);
        handleToast(
          toastId,
          'loading',
          t(
            'menu.toast.bulkAvailabilty.loading.title',
            'Bulk Items Availability',
          ),
          t('menu.toast.bulkAvailabilty.loading.message', 'Loading....'),
        );
        setIsLoading(true);
        const itemsUUIDs = bulkItems.map((item: Item) => {
          return {
            itemUUID: item.itemUUID,
            availability,
            ...(!availability && { unavailabilityIntervalEnum }),
          };
        });

        const response = await changeBulkItemsAvailability(
          branch.branchUUID,
          itemsUUIDs,
        );

        const names: string[] = [];
        response.map((el: any, i: number) => {
          const { status } = el;

          if (status === 'rejected') {
            i18n.language === 'ar'
              ? names.push(searchedItems[i].name.arName)
              : names.push(searchedItems[i].name.enName);
            const index = bulkItems.indexOf(searchedItems[i]);
            bulkItems.splice(index, 1);
          }
        });

        bulkItems.map(
          async (item) => await updateSelectedMenu(item.itemUUID, availability),
        );

        trackEvent('Action: Bulk Item Availability', {
          menu_id: menu?.uuid,
          branch_id: branch?.branchUUID,
          items: bulkItems.map((item) => item.name.enName),
          status: availability ? 'Avail' : 'Unavail',
          ...(!availability && { time: unavailabilityIntervalEnum }),
        });

        if (names.length < bulkItems.length) {
          handleToast(
            toastId,
            'success',
            t(
              'menu.toast.bulkAvailabilty.loading.title',
              'Bulk Items Availability',
            ),
            t(
              'menu.toast.bulkAvailabilty.success.message',
              'Bulk Items availability updated successfully',
            ),
          );
        }

        if (names.length) {
          handleToast(
            toastId,
            'error',
            t('menu.common.toast.error.title', 'Something went wrong!'),
            t('menu.toast.bulkAvailablity.error.message', {
              defaultValue:
                'Something went wrong while Items updating {{value}}, Please try again later.',
              value: names.toString(),
            }),
          );
        }
      } catch (error) {
        handleToast(
          toastId,
          'error',
          t('menu.common.toast.error.title', 'Something went wrong!'),
          t('menu.common.toast.error.message', {
            defaultValue:
              'Something went wrong while Item {{value}}, Please try again later.',
            value: availability ? 'disabled' : 'enabled',
          }),
        );
      } finally {
        setIsLoading(false);
        resetInputs();
      }
    }
  };

  const handleUnavailAll = () => {
    setOpenConfirm(false);
    setIsOpen(true);
  };

  return (
    <ScrollView style={styles.root}>
      {loading && (
        <View style={styles.loading}>
          <ActivityIndicator size="large" />
        </View>
      )}
      {menu && !loading && (
        <>
          <View style={styles.updateBtnContainer}>
            <View style={styles.searchWrapper}>
              <Input
                placeholder={t('menu.searchMenu', {
                  defaultValue: 'Search Menu',
                })}
                value={searchText}
                onChange={setSearchText}
                style={{ input: styles.searchInput }}
              />
              {searchText.length > 0 && (
                <TouchableOpacity
                  onPress={() => setSearchText('')}
                  style={styles.closeBtn}>
                  <Icon name="close" size={14} />
                </TouchableOpacity>
              )}
            </View>
            {searchText && BULK_ITEMS_AVAILABILITY && (
              <View style={styles.toggles}>
                <View style={styles.item}>
                  <Switch
                    trackColor={{
                      false: theme.palette.grey[400],
                      true: theme.palette.primary[600],
                    }}
                    thumbColor={theme.palette.base[100]}
                    onValueChange={() => {
                      setUnavailAll(false);
                      setOpenConfirm(true);
                    }}
                    value={unavailAll}
                    {...Platform.select({
                      web: {
                        activeThumbColor: theme.palette.base[100],
                      },
                    })}
                  />
                  <Typography
                    weight="medium"
                    variant="primary"
                    style={styles.bulkToggle}>
                    {t('menu.unavailAllItems', 'Unavail all items')}
                  </Typography>
                </View>
                <View style={[styles.item, !isMobile && styles.bulkToggle]}>
                  <Switch
                    trackColor={{
                      false: theme.palette.grey[400],
                      true: theme.palette.primary[600],
                    }}
                    thumbColor={theme.palette.base[100]}
                    onValueChange={() => {
                      setAvailAll(true);
                      setOpenConfirm(true);
                    }}
                    value={availAll}
                    {...Platform.select({
                      web: {
                        activeThumbColor: theme.palette.base[100],
                      },
                    })}
                  />
                  <Typography
                    weight="medium"
                    variant="primary"
                    style={styles.bulkToggle}>
                    {t('menu.availAllItems', 'Avail all items')}
                  </Typography>
                </View>
              </View>
            )}
            <Button
              variant="secondary"
              startIcon={<Icon name="edit" size={20} style={styles.icon} />}
              onPress={() => requestUpdate(menu)}
              fullWidth={isMobile}>
              {t('menu.requestUpdateBtn', {
                defaultValue: 'Request Updates',
              })}
            </Button>
          </View>
          <List.AccordionGroup>
            {searchText.length > 0 ? searchMenu() : renderMenu(menu.categories)}
          </List.AccordionGroup>
        </>
      )}
      <Modal
        open={isOpen}
        onRequestClose={resetInputs}
        dismissOnBackdropPress={!loading}>
        <ModalContentLayout
          icon={<CircledIcon icon="close" preset="error" />}
          title={t(
            'orderView.rejectOrder.step2.title',
            'Item(s) not available',
          )}
          subtitle={t(
            'orderView.rejectOrder.step2.subTitle',
            'Choose the unavailable item and the unavailable duration.',
          )}
          actions={[
            <Button
              variant="primary"
              loading={isLoading}
              disabled={
                loading ||
                (!itemAvail.availability &&
                  !itemAvail.unavailabilityIntervalEnum)
              }
              onPress={() =>
                unavailAll
                  ? handleItemUnavailability(itemAvail)
                  : handleBulkItemsUnavailable()
              }>
              {t(
                'orderView.rejectOrder.step2.confirmBtn',
                'Confirm Item(s) not avaiable',
              )}
            </Button>,
            <Button
              variant="secondary"
              onPress={() => {
                resetInputs();
              }}>
              {t('orderView.rejectOrder.cancelBtn', 'Cancel')}
            </Button>,
          ]}
          content={
            <View>
              <RadioGroup
                size={6}
                items={itemUnavailabilityInterval}
                onItemPress={(time) => {
                  setDuration(time);
                  setItemAvail((current) => ({
                    ...current,
                    unavailabilityIntervalEnum: time.value,
                  }));
                }}
                value={duration}
                disabled={isLoading}
              />
            </View>
          }
        />
      </Modal>
      <Modal
        open={openConfirm}
        onRequestClose={resetConfirmationInputs}
        dismissOnBackdropPress={!loading}>
        <ModalContentLayout
          icon={<CircledIcon icon="info-circle" preset="warning" />}
          actions={[
            <Button
              variant="primary"
              onPress={() =>
                !unavailAll ? handleUnavailAll() : handleBulkItemsAvailable()
              }>
              {t('menu.common.okBtn', 'Ok')}
            </Button>,
            <Button
              variant="secondary"
              onPress={() => resetConfirmationInputs()}>
              {t('orderView.rejectOrder.cancelBtn', 'Cancel')}
            </Button>,
          ]}
          content={
            <Typography>
              {t(
                'menu.confirmBuldAvailabilty',
                'The action will effect all items returned from search, Are you sure to proceed?',
              )}
            </Typography>
          }
        />
      </Modal>
    </ScrollView>
  );
};
